import { createAnalytics } from '../analytics';
import { prepareNetworkEventProps } from '../analytics/utils';
import type { DecodedClientToken } from '../core/ClientTokenHandler';
import type { ClientConfiguration } from '../models/ClientConfiguration';
import { get } from '../utils/http';
import { uuid } from '../utils/uuid';
import { loadPrimer } from './loadPrimer';

type PrimerClient = typeof import('../Primer');
type Tail<T extends unknown[]> = T extends [any, any, ...infer P] ? P : never;
type PrimerFunctions = Exclude<keyof PrimerClient, 'SDK_VERSION'>;
type ArgsOf<T extends PrimerFunctions> = Tail<Parameters<PrimerClient[T]>>;

export const SDK_VERSION = process.env.PRIMER_SDK_VERSION as string;

export const createHeadless = lazyLoad('createHeadless');
export const showExpressCheckout = lazyLoad('showExpressCheckout');
export const showUniversalCheckout = lazyLoad('showUniversalCheckout');
export const showVaultManager = lazyLoad('showVaultManager');

const messageType = 'LOAD_PERFORMANCE';

const checkoutSessionId = uuid();
let analytics = createAnalytics({ checkoutSessionId });

analytics.messageEvent({
  message: 'parsed',
  messageType,
  // eslint-disable-next-line compat/compat
  now: performance.now?.(),
});

function lazyLoad<FnName extends PrimerFunctions>(name: FnName) {
  return async (...[clientToken, options]: ArgsOf<FnName>) => {
    analytics.sdkFunctionEvent({ name });
    analytics.messageEvent({
      message: 'create',
      messageType,
      // eslint-disable-next-line compat/compat
      now: performance.now?.(),
    });

    const [config, client] = await Promise.all([
      getConfiguration(clientToken),
      loadPrimer(),
    ]);

    return client[name](config, checkoutSessionId, clientToken, options);
  };
}

async function getConfiguration(clientToken: string) {
  const {
    accessToken,
    configurationUrl,
    analyticsUrlV2: analyticsUrl,
  } = decodeClientToken(clientToken);

  analytics = createAnalytics({ checkoutSessionId, url: analyticsUrl });

  const url = `${configurationUrl}/?withDisplayMetadata=true`;
  const id = uuid();
  const payload = { method: 'get' as any, url };
  analytics.networkCallEvent(prepareNetworkEventProps(id, payload));

  const config = await get<ClientConfiguration>(url, {
    'primer-client-token': accessToken,
    'primer-sdk-client': 'WEB',
    'primer-sdk-version': SDK_VERSION,
    'x-api-version': '2.1',
  });

  const { primerAccountId } = config;
  const { clientSessionId } = config.clientSession;
  analytics = createAnalytics({
    checkoutSessionId,
    clientSessionId,
    primerAccountId,
    url: analyticsUrl,
  });

  analytics.networkCallEvent(
    prepareNetworkEventProps(id, payload, { data: config }),
  );

  return config;
}

function decodeClientToken(clientToken: string): DecodedClientToken {
  const tokens = clientToken.split('.');
  const encoded = tokens.length === 1 ? tokens[0] : tokens[1];

  return JSON.parse(window.atob(encoded));
}
